import { hasPermission, type PermissionScope } from '@nexus/core';
import {
  hashKey,
  queryOptions,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api } from '../api/client';
import type { SessionInfo } from '../api/types';

export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: ({ signal }) => api.get<SessionInfo>('/auth/session', { signal }),
  staleTime: 60_000,
  // Modules and permissions an administrator changed in another tab show when this one comes back.
  refetchOnWindowFocus: 'always',
  retry: false,
});

const activeModules = (session: SessionInfo | undefined) =>
  session ? [...session.modules].sort().join(',') : undefined;

/**
 * Calls `listener` when a fresh session lists other active modules than the one before, e.g. after
 * an administrator switched a module. Not on the first session after start or login.
 */
export function onModulesChanged(client: QueryClient, listener: () => void): () => void {
  const sessionHash = hashKey(sessionQuery.queryKey);
  let known = activeModules(client.getQueryData(sessionQuery.queryKey));
  return client.getQueryCache().subscribe((event) => {
    if (event.query.queryHash !== sessionHash) return;
    if (event.type === 'removed') known = undefined;
    if (event.type !== 'updated' || event.action.type !== 'success') return;
    const next = activeModules(event.query.state.data as SessionInfo | undefined);
    if (known !== undefined && next !== known) listener();
    known = next;
  });
}

/** The session of the logged-in person. Only inside the app area, which loads it first. */
export function useSession(): SessionInfo {
  return useSuspenseQuery(sessionQuery).data;
}

export function canIn(session: SessionInfo, permission: string, scope?: PermissionScope): boolean {
  return (
    hasPermission(session.permissions, permission, scope) ||
    hasPermission(session.platformPermissions, permission, scope)
  );
}

/**
 * Whether the person has a permission. Only for showing and hiding: the API checks every call
 * itself (CLAUDE.md, "Das Frontend versteckt, das Backend entscheidet").
 */
export function useCan(permission: string, scope?: PermissionScope): boolean {
  return canIn(useSession(), permission, scope);
}

export function Can({
  permission,
  scope,
  children,
}: {
  permission: string;
  scope?: PermissionScope;
  children: ReactNode;
}) {
  return useCan(permission, scope) ? children : null;
}

/** Reloads everything that depends on the session, e.g. after a company switch. */
export function useResetSession(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
    await client.invalidateQueries({ queryKey: ['session'] });
  };
}
